import React, { useState, useEffect, useRef } from 'react';
import { Heart, Plus, X, Trash2 } from 'lucide-react';
import api from '../components/api';
import { toast } from 'react-toastify';
import useGlobale from '../components/global';
import Sidebar from '../components/SideBar';
import Header from '../components/Header';
import DemandeurMenu from '../components/DemandeurMenu';

const CentreInteretPage = () => {
    const { user } = useGlobale();
    const [loading, setLoading]             = useState(true);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [centres, setCentres]             = useState([]);
    const [inputVal, setInputVal]           = useState('');
    const [saving, setSaving]               = useState(false);
    const [showConfirm, setShowConfirm]     = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        const handleResize = () => setIsSidebarOpen(window.innerWidth >= 1024);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => { fetchCentres(); }, []);

    const fetchCentres = async () => {
        setLoading(true);
        try {
            const res = await api.get('demandeur/centres-interet/');
            setCentres(res.data);
        } catch {
            toast.error("Erreur lors du chargement");
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async () => {
        const nom = inputVal.trim();
        if (!nom) return;
        if (centres.some(c => c.nom.toLowerCase() === nom.toLowerCase())) {
            toast.warning("Ce centre d'intérêt existe déjà");
            return;
        }
        setSaving(true);
        try {
            const res = await api.post('demandeur/centres-interet/', { nom });
            setCentres(prev => [...prev, res.data]);
            setInputVal('');
            inputRef.current?.focus();
        } catch (err) {
            const msg = err.response?.data?.nom?.[0] || "Erreur lors de l'ajout";
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async (id) => {
        try {
            await api.delete(`demandeur/centres-interet/${id}/`);
            setCentres(prev => prev.filter(c => c.id !== id));
        } catch {
            toast.error("Erreur lors de la suppression");
        }
    };

    const handleDeleteAll = async () => {
        try {
            await api.delete('demandeur/centres-interet/');
            setCentres([]);
            setShowConfirm(false);
            toast.success("Tous les centres d'intérêt ont été supprimés");
        } catch {
            toast.error("Erreur lors de la suppression");
        }
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-orange-500"></div>
                </div>
            );
        }

        return (
            <>
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-[#437011] mb-1">Centres d'intérêt</h1>
                    <p className="text-gray-500 text-sm">Saisissez librement vos centres d'intérêt pour enrichir votre profil</p>
                </div>

                <div className="bg-white rounded-xl shadow-md p-6">
                    {/* Champ de saisie */}
                    <div className="flex gap-2 mb-5">
                        <input
                            ref={inputRef}
                            type="text"
                            value={inputVal}
                            onChange={e => setInputVal(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleAdd()}
                            placeholder="Ex : Lecture, Sport, Voyages..."
                            className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                        <button
                            onClick={handleAdd}
                            disabled={saving || !inputVal.trim()}
                            className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white rounded-lg text-sm font-medium transition"
                        >
                            <Plus size={16} /> Ajouter
                        </button>
                    </div>

                    {/* Tags */}
                    {centres.length > 0 ? (
                        <>
                            <div className="flex flex-wrap gap-2 mb-5">
                                {centres.map(c => (
                                    <span key={c.id} className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 bg-orange-50 text-orange-700 border border-orange-200 rounded-full text-sm font-medium">
                                        <Heart size={13} className="fill-orange-400 text-orange-400 flex-shrink-0" />
                                        {c.nom}
                                        <button
                                            onClick={() => handleRemove(c.id)}
                                            className="ml-0.5 p-0.5 rounded-full hover:bg-orange-200 transition"
                                            title="Supprimer"
                                        >
                                            <X size={13} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                            <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                                <p className="text-xs text-gray-400">{centres.length} centre{centres.length > 1 ? 's' : ''} d'intérêt</p>
                                <button onClick={() => setShowConfirm(true)} className="text-xs text-red-400 hover:text-red-600 transition">
                                    Tout supprimer
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-8">
                            <div className="h-14 w-14 rounded-full bg-orange-100 flex items-center justify-center text-orange-400 mx-auto mb-3">
                                <Heart size={28} strokeWidth={1.5} />
                            </div>
                            <p className="text-gray-500 text-sm">Aucun centre d'intérêt ajouté.<br />Commencez à taper ci-dessus !</p>
                        </div>
                    )}
                </div>

                {/* Modal confirmation suppression */}
                {showConfirm && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 text-center">
                            <div className="w-11 h-11 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
                                <Trash2 className="h-5 w-5 text-red-600" />
                            </div>
                            <h3 className="text-base font-semibold text-gray-900 mb-1">Confirmer la suppression</h3>
                            <p className="text-gray-500 text-sm mb-5">Voulez-vous supprimer tous vos centres d'intérêt ?</p>
                            <div className="flex justify-center gap-3">
                                <button onClick={() => setShowConfirm(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">Annuler</button>
                                <button onClick={handleDeleteAll} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">Supprimer tout</button>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    };

    return (
        <div className="flex min-h-screen bg-[#F4F8E9]">
            <Sidebar isOpen={isSidebarOpen} menuItems={DemandeurMenu()} onClose={() => setIsSidebarOpen(false)} />
            <div className="flex-1 flex flex-col w-full">
                <Header isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} user={user} />
                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 pb-20 lg:pb-8 w-full">
                    {renderContent()}
                </main>
            </div>
        </div>
    );
};

export default CentreInteretPage;
